import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * Hidden internal route: /meta-preview
 *
 * Renders the exact <meta> / <title> / <link rel="canonical"> values currently
 * present in <head>, plus a live preview of the og:image. Not linked from
 * navigation and explicitly noindex/nofollow.
 *
 * Use this to confirm what social crawlers would see for any given page:
 * open the target page in one tab, then navigate to /meta-preview to inspect
 * the tags that were emitted. (Note: TanStack Router head() is per-route, so
 * the values you see reflect /meta-preview's own head merged with the root
 * defaults. To inspect another route's meta, view page source on that route.)
 */
export const Route = createFileRoute("/meta-preview")({
  head: () => ({
    meta: [
      { title: "Meta Preview (internal)" },
      { name: "description", content: "Internal tool. Renders current head tags for QA." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MetaPreviewPage,
});

type Tag = { kind: "meta" | "link" | "title"; key: string; value: string };

function readHeadTags(): Tag[] {
  if (typeof document === "undefined") return [];
  const tags: Tag[] = [];

  tags.push({ kind: "title", key: "<title>", value: document.title });

  document.querySelectorAll("head meta").forEach((el) => {
    const m = el as HTMLMetaElement;
    const name = m.getAttribute("name");
    const property = m.getAttribute("property");
    const httpEquiv = m.getAttribute("http-equiv");
    const charset = m.getAttribute("charset");
    const itemprop = m.getAttribute("itemprop");
    const key =
      property ??
      name ??
      (httpEquiv ? `http-equiv:${httpEquiv}` : null) ??
      (charset ? "charset" : null) ??
      (itemprop ? `itemprop:${itemprop}` : null);
    if (!key) return;
    const value = charset ?? m.getAttribute("content") ?? "";
    tags.push({ kind: "meta", key, value });
  });

  document.querySelectorAll('head link[rel="canonical"], head link[rel="alternate"]').forEach((el) => {
    const l = el as HTMLLinkElement;
    tags.push({
      kind: "link",
      key: `link:${l.getAttribute("rel")}${l.getAttribute("hreflang") ? `[${l.getAttribute("hreflang")}]` : ""}`,
      value: l.getAttribute("href") ?? "",
    });
  });

  return tags;
}

function pick(tags: Tag[], keys: string[]): string {
  for (const k of keys) {
    const hit = tags.find((t) => t.key === k);
    if (hit && hit.value) return hit.value;
  }
  return "";
}

function MetaPreviewPage() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [url, setUrl] = useState<string>("");

  useEffect(() => {
    setTags(readHeadTags());
    setUrl(window.location.href);
  }, []);

  const title = pick(tags, ["<title>"]);
  const description = pick(tags, ["description"]);
  const ogTitle = pick(tags, ["og:title"]) || title;
  const ogDescription = pick(tags, ["og:description"]) || description;
  const ogImage = pick(tags, ["og:image"]);
  const ogImageAlt = pick(tags, ["og:image:alt"]);
  const twitterTitle = pick(tags, ["twitter:title"]) || ogTitle;
  const twitterDescription = pick(tags, ["twitter:description"]) || ogDescription;
  const twitterImage = pick(tags, ["twitter:image"]) || ogImage;
  const twitterCard = pick(tags, ["twitter:card"]);
  const canonical = pick(tags, ["link:canonical"]);
  const robots = pick(tags, ["robots"]);

  const groups: { label: string; items: Tag[] }[] = [
    { label: "Title & description", items: tags.filter((t) => t.key === "<title>" || t.key === "description" || t.key === "robots" || t.key === "canonical" || t.key === "link:canonical") },
    { label: "Open Graph", items: tags.filter((t) => t.key.startsWith("og:")) },
    { label: "Twitter", items: tags.filter((t) => t.key.startsWith("twitter:")) },
    { label: "Other", items: tags.filter((t) => !t.key.startsWith("og:") && !t.key.startsWith("twitter:") && t.key !== "<title>" && t.key !== "description" && t.key !== "robots" && t.key !== "link:canonical") },
  ];

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Internal / not indexed</p>
        <h1 className="mt-2 text-3xl font-semibold text-foreground">Meta Preview</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Live values from <code className="rounded bg-muted px-1 py-0.5 text-xs">document.head</code> on{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">{url || "…"}</code>.
        </p>
      </header>

      <section className="mb-12">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Social card preview
        </h2>
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {ogImage ? (
            <img
              src={ogImage}
              alt={ogImageAlt || "og:image"}
              className="aspect-[1200/630] w-full object-cover"
            />
          ) : (
            <div className="flex aspect-[1200/630] w-full items-center justify-center bg-muted text-xs text-muted-foreground">
              No og:image
            </div>
          )}
          <div className="space-y-1 border-t border-border p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{canonical || url}</p>
            <p className="text-base font-semibold text-foreground">{ogTitle || "(no title)"}</p>
            <p className="text-sm text-muted-foreground">{ogDescription || "(no description)"}</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <Row label="twitter:card" value={twitterCard} />
          <Row label="twitter:title" value={twitterTitle} />
          <Row label="twitter:description" value={twitterDescription} />
          <Row label="twitter:image" value={twitterImage} />
          <Row label="og:image:alt" value={ogImageAlt} />
          <Row label="robots" value={robots} />
        </div>
      </section>

      {groups.map((g) =>
        g.items.length === 0 ? null : (
          <section key={g.label} className="mb-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {g.label}
            </h2>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {g.items.map((t, i) => (
                    <tr key={`${t.key}-${i}`} className="border-b border-border last:border-0">
                      <th className="w-1/3 bg-muted/50 px-3 py-2 text-left align-top font-mono text-xs text-muted-foreground">
                        {t.key}
                      </th>
                      <td className="break-all px-3 py-2 align-top font-mono text-xs text-foreground">
                        {t.value || <span className="text-muted-foreground">(empty)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ),
      )}

      <footer className="mt-12 border-t border-border pt-6 text-xs text-muted-foreground">
        <p>
          Note: values shown are for <strong>this</strong> route. To inspect another page's tags, open that
          page and view page source, or run this route inside a dev tool that scrapes the target URL.
        </p>
      </footer>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded border border-border bg-card px-3 py-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="break-all font-mono text-xs text-foreground">{value || "(empty)"}</span>
    </div>
  );
}
